import { Component } from '@theme/component';

/**
 * Fills `[start_date]` / `[end_date]` placeholders in every `[data-dynamic-date]`
 * descendant, based on `data-min-days` / `data-max-days` counted from today,
 * skipping configured weekdays and MM-DD dates (they don't count as days).
 *
 * Root attributes:
 *   data-date-format   mm_dd | day_mm_dd | dd_mm | dd_mm_no_dot | day_dd_mm | day_dd_mm_numeric | dd_mm_numeric
 *   data-day-labels    "Mon, Tue, ..." (7, Monday first)
 *   data-month-labels  "Jan, Feb, ..." (12)
 *   data-days-to-skip  "saturday,sunday"
 *   data-dates-to-skip "12-25,01-01"
 */
class DynamicDatesComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.render();
  }

  render() {
    const dayLabels = this.#list(this.dataset.dayLabels, ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']);
    const monthLabels = this.#list(this.dataset.monthLabels, [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
    ]);
    const skipDays = this.#skipDayIndexes(this.dataset.daysToSkip ?? '');
    const skipDates = new Set(
      (this.dataset.datesToSkip ?? '')
        .split(',')
        .map((d) => d.trim())
        .filter(Boolean)
    );
    const format = this.dataset.dateFormat || 'mm_dd';

    for (const el of this.querySelectorAll('[data-dynamic-date]')) {
      if (!(el instanceof HTMLElement)) continue;
      const template = el.dataset.text ?? '';
      if (!template.includes('[start_date]') && !template.includes('[end_date]')) continue;
      const start = this.#addDays(Number(el.dataset.minDays) || 0, skipDays, skipDates);
      const end = this.#addDays(Number(el.dataset.maxDays) || 0, skipDays, skipDates);
      const html = template
        .split('[start_date]').join(this.#format(start, format, dayLabels, monthLabels))
        .split('[end_date]').join(this.#format(end, format, dayLabels, monthLabels));
      if (el.innerHTML !== html) el.innerHTML = html;
    }
  }

  /**
   * @param {string | undefined} raw
   * @param {string[]} fallback
   */
  #list(raw, fallback) {
    const items = (raw ?? '').split(',').map((s) => s.trim()).filter(Boolean);
    return items.length === fallback.length ? items : fallback;
  }

  /** @param {string} raw - "saturday, sunday" → JS getDay() indexes */
  #skipDayIndexes(raw) {
    const names = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const set = new Set();
    for (const part of raw.toLowerCase().split(',')) {
      const name = part.trim();
      const index = names.findIndex((n) => n === name || n.slice(0, 3) === name.slice(0, 3));
      if (name && index >= 0) set.add(index);
    }
    return set;
  }

  /**
   * @param {Date} date
   * @param {Set<number>} skipDays
   * @param {Set<string>} skipDates
   */
  #isSkipped(date, skipDays, skipDates) {
    if (skipDays.has(date.getDay())) return true;
    const mmdd = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return skipDates.has(mmdd);
  }

  /**
   * Adds `days` counting days, skipped days don't count. 0 days = today.
   * @param {number} days
   * @param {Set<number>} skipDays
   * @param {Set<string>} skipDates
   */
  #addDays(days, skipDays, skipDates) {
    const date = new Date();
    date.setHours(12, 0, 0, 0);
    let remaining = Math.max(0, Math.round(days));
    if (remaining === 0) return date;
    let guard = 0;
    while (remaining > 0 && guard < 400) {
      date.setDate(date.getDate() + 1);
      guard += 1;
      if (!this.#isSkipped(date, skipDays, skipDates)) remaining -= 1;
    }
    return date;
  }

  /** @param {number} n */
  #ordinal(n) {
    const mod100 = n % 100;
    if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
    switch (n % 10) {
      case 1: return `${n}st`;
      case 2: return `${n}nd`;
      case 3: return `${n}rd`;
      default: return `${n}th`;
    }
  }

  /**
   * @param {Date} date
   * @param {string} format
   * @param {string[]} dayLabels - Monday first
   * @param {string[]} monthLabels
   */
  #format(date, format, dayLabels, monthLabels) {
    const dayName = dayLabels[(date.getDay() + 6) % 7];
    const month = monthLabels[date.getMonth()];
    const d = date.getDate();
    const dd = String(d).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    switch (format) {
      case 'day_mm_dd': return `${dayName}, ${month} ${this.#ordinal(d)}`;
      case 'day_dd_mm': return `${dayName}, ${d}. ${month}`;
      case 'dd_mm': return `${d}. ${month}`;
      case 'dd_mm_no_dot': return `${d} ${month}`;
      case 'day_dd_mm_numeric': return `${dayName}, ${dd}. ${mm}.`;
      case 'dd_mm_numeric': return `${dd}. ${mm}.`;
      case 'mm_dd':
      default: return `${month} ${this.#ordinal(d)}`;
    }
  }
}

if (!customElements.get('dynamic-dates-component')) {
  customElements.define('dynamic-dates-component', DynamicDatesComponent);
}
